import { images } from "@/data/images";

// EXPERIENCE PAGE — a coaching portfolio / case study, not a résumé.
export const resume = {
  heroSubtitle:
    "Building programs. Developing people. Never stopping as a student of the game.",

  // Centerpiece role
  uhill: {
    org: "University Hill Secondary",
    position: "Head of Basketball Operations & Varsity Head Coach",
    years: "2021 – Present",
    tagline:
      "I took a Grade 9 group and stayed with them. Culture and development first, but it took a village for the program to be successful. From graduated players, assistant coaches, managers, school staff and much more. Results followed.",
    image: images.teamAlt,
  },

  // Program growth across five seasons (league wins drive the chart).
  programGrowth: {
    subhead: "Five seasons at University Hill. Built on development, not shortcuts.",
    peakWins: 10,
    seasons: [
      {
        season: "2021–2022",
        team: "Grade 9 Boys",
        regular: "0–7",
        exhibition: null,
        leagueWins: 0,
        highlights: [
          "First season coaching.",
          "Quickly realized loving basketball wasn't enough to become a great coach.",
          "Started studying how to teach, communicate, and build a program.",
          "Became the foundation of my coaching philosophy.",
        ],
      },
      {
        season: "2022–2023",
        team: "Grade 10 Boys",
        regular: "1–9",
        exhibition: "1 win",
        leagueWins: 1,
        highlights: [
          "Moved up with the same core group.",
          "Earned the program's first regular-season win.",
          "Lost several games by just a few possessions.",
          "Kept building the culture and foundation.",
        ],
      },
      {
        season: "2023–2024",
        team: "First Varsity Season",
        regular: "1–10",
        exhibition: "2 wins",
        leagueWins: 1,
        highlights: [
          "Promoted to Varsity Head Coach.",
          "Lost several key players before the season began.",
          "Continued building standards and culture.",
          "Focus remained on long-term program development.",
        ],
      },
      {
        season: "2024–2025",
        team: "Varsity Head Coach",
        regular: "4–7",
        exhibition: "6 wins",
        leagueWins: 4,
        highlights: [
          "Big jump in competitiveness across the program.",
          "Started winning the games we used to lose close.",
          "Kept investing in player development and culture.",
        ],
      },
      {
        season: "2025–2026",
        team: "Varsity Head Coach",
        regular: "10–3",
        exhibition: "0 wins",
        leagueWins: 10,
        badge: "BC Zone Championships",
        highlights: [
          "Intentionally scheduled one of the toughest exhibition schedules available by competing in Gold Division tournaments.",
          "Used every exhibition game as preparation instead of chasing exhibition wins.",
          "League semifinalists.",
          "Lost to the eventual league champions by two points.",
          "Qualified for the BC Zone Championships.",
          "Finished 7th at the BC Zone Championships, matching the school's best finish since 2022 and tying one of the strongest results since the school's provincial championship in 1985.",
          "Developed the program's first Prep School and Nike EYBL athlete.",
        ],
      },
    ],
  },

  // Empower Basketball — portfolio block
  empower: {
    org: "Empower Basketball",
    position: "Head Coach",
    image: images.coachSideline,
    intro:
      "Year-round club coaching. Boys and girls, U10 to U17, across every division.",
    stats: [
      { value: "3+", label: "Years Coaching" },
      { value: "70+", label: "Club Athletes" },
      { value: "15+", label: "Bronze Titles" },
      { value: "5+", label: "Silver Titles" },
      { value: "3+", label: "Gold Titles" },
    ],
    facts: ["Year-Round", "Boys & Girls", "U10–U17", "Bronze · Silver · Gold Divisions"],
    development: [
      "Coached athletes from U10 through U17.",
      "Worked with both boys and girls.",
      "Adapted to different ages, experience levels, and learning styles.",
      "Prepared athletes for school tryouts and competitive club play.",
      "Most athletes have earned spots on their school teams.",
      "Designed age-appropriate development plans.",
    ],
    training: [
      { icon: "Dribbble", label: "Skill Development" },
      { icon: "Brain", label: "Basketball IQ & Decisions" },
      { icon: "Zap", label: "Speed Training" },
      { icon: "Dumbbell", label: "Strength & Conditioning" },
      { icon: "Users", label: "Team Concepts" },
      { icon: "TrendingUp", label: "Confidence & Leadership" },
    ],
    camps:
      "I've coached multiple basketball camps throughout the year, working with athletes of different ages and experience levels.",
  },

  hoopers: {
    org: "Hooper's Delight Training",
    position: "Founder & Lead Trainer",
    intro:
      "Hooper's Delight Training is an invite development program for committed boys and girls looking to take the next step in their game. I work with current post-secondary athletes as well as high school players pursuing opportunities at the university or college level. Training is intentionally kept small so development can be tailored to each athlete, their goals, and where they are in their game.",
    areas: [
      {
        icon: "Sparkles",
        title: "Individualized Development",
        desc: "Training is built around each athlete's needs, strengths, and areas for growth.",
      },
      {
        icon: "Dribbble",
        title: "Game-Based Training",
        desc: "Developing skills and decision-making that translate to competition.",
      },
      {
        icon: "Users",
        title: "Small & Intentional",
        desc: "Invite-only individual and small-group environments allow for more feedback and attention.",
      },
      {
        icon: "GraduationCap",
        title: "Preparing for the Next Level",
        desc: "Helping high school athletes prepare for post-secondary basketball while continuing to develop athletes already competing at that level.",
      },
    ],
  },

  falcons: {
    org: "West Coast Falcons Basketball Club",
    position: "Founder & Head Coach",
    image: images.falconsCoaches,
    overview: [
      "A nonprofit club founded to give young athletes competitive basketball regardless of their finances.",
      "The mission: make club basketball more accessible while helping athletes grow on and off the court.",
    ],
    stats: [
      { value: "35+", label: "Athletes Coached" },
      { value: "9+", label: "Tournaments" },
    ],
    facts: ["Nonprofit Basketball Club", "Year-Round Development", "Removing Financial Barriers to Club Basketball"],
    focus: [
      "Long-term athlete development",
      "Competitive opportunities",
      "Basketball skill development",
      "Character and leadership",
      "Making club basketball more accessible",
    ],
  },

  education: {
    school: "Langara College",
    program: "Diploma in Kinesiology",
    sportScience: [
      { icon: "HeartPulse", title: "Exercise Physiology", desc: "How the body produces and sustains energy." },
      { icon: "Dumbbell", title: "Strength & Conditioning", desc: "Building resilient, explosive athletes." },
      { icon: "Activity", title: "Motor Learning", desc: "How skills are acquired and retained." },
      { icon: "Brain", title: "Sport Psychology", desc: "The mental side of performance." },
      { icon: "Gauge", title: "Human Performance", desc: "Getting the most out of the body." },
    ],
    beyondClassroom: [
      { icon: "Clapperboard", title: "Film Study", desc: "Breaking down our games and elite teams." },
      { icon: "Headphones", title: "Coaching Podcasts", desc: "Learning from coaches on the move." },
      { icon: "BookOpen", title: "Basketball Books", desc: "Studying the minds who shaped the game." },
      { icon: "Users", title: "Coaches at Every Level", desc: "From grassroots to the college game." },
      { icon: "FlaskConical", title: "Sports Science Research", desc: "Keeping practice grounded in evidence." },
      { icon: "GraduationCap", title: "Continuing Education", desc: "Always adding to the toolbox." },
    ],
    applying: [
      { from: "Exercise Physiology", to: "Strength & Conditioning", icon: "Dumbbell" },
      { from: "Motor Learning", to: "Practice Design", icon: "ClipboardList" },
      { from: "Sport Psychology", to: "Leadership & Confidence", icon: "Sparkles" },
      { from: "Human Performance", to: "Speed Development", icon: "Zap" },
    ],
  },
};
